import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import AppImage from '../../components/AppImage';
import { Users, Luggage } from 'lucide-react';
import { tourPackageData } from '../../data/tourPackageData';

const TourPackageBooking = () => {
  const { cityName, destination } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDay, setSelectedDay] = useState('3-days');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName, destination]);

  const routeKey = `${cityName?.toLowerCase()}-${destination?.toLowerCase()}`;
  const packageData = tourPackageData?.[routeKey];

  // Extract search query parameters
  const searchParams = new URLSearchParams(location?.search);
  const searchFrom = searchParams?.get('searchFrom');
  const searchTo = searchParams?.get('searchTo');

  // Use search query for display if available, otherwise use package data
  const displayFrom = searchFrom || packageData?.from || cityName?.charAt(0)?.toUpperCase() + cityName?.slice(1);
  const displayTo = searchTo || packageData?.to || destination?.charAt(0)?.toUpperCase() + destination?.slice(1);

  // If package not found, create a default package structure with basic vehicles
  const defaultVehicles = [
    {
      type: 'Maruti Dzire',
      luggage: 3,
      seats: 4,
      price: 8000,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_109a9f6bf-1768379759524.png",
      alt: `White Maruti Dzire sedan car for ${displayFrom} to ${displayTo} tour`
    },
    {
      type: 'Brezza',
      luggage: 4,
      seats: 4,
      price: 9500,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de88d83e-1768380471356.png",
      alt: `Maruti Brezza compact SUV for ${displayFrom} to ${displayTo} journey`
    },
    {
      type: 'Maruti Ertiga',
      luggage: 4,
      seats: 6,
      price: 10500,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cecda4a3-1768145557954.png",
      alt: `Maruti Ertiga MPV with spacious seating for ${displayTo} tour`
    },
    {
      type: 'Innova Crysta',
      luggage: 6,
      seats: 6,
      price: 13500,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b3582ff-1764720422661.png",
      alt: `Toyota Innova Crysta luxury MPV for premium ${displayTo} travel`
    },
    {
      type: 'Innova Hycross',
      luggage: 5,
      seats: 6,
      price: 16500,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_128b5b542-1768145556724.png",
      alt: `Toyota Innova Hycross latest model for comfortable ${displayTo} trip`
    }
  ];

  const effectivePackageData = packageData || {
    from: displayFrom,
    to: displayTo,
    companyName: 'Advait Tour and Travels',
    phone: '+91-79986-66660',
    days: [
      { id: '2-days', label: '2 Days' },
      { id: '3-days', label: '3 Days' },
      { id: '4-days', label: '4 Days' },
      { id: '5-days', label: '5 Days' },
      { id: '6-days', label: '6 Days' },
      { id: '7-days', label: '7 Days' },
      { id: '8-days', label: '8 Days' }
    ],
    vehicles: defaultVehicles,
    visitorsGuide: {
      title: `Experience the Journey from ${displayFrom} to ${displayTo}`,
      intro: `Advait Tour and Travels provides premium taxi services for your ${displayFrom} to ${displayTo} journey. Travel in comfort with our well-maintained vehicles and professional drivers.`,
      sections: [
        {
          title: 'Why Choose Advait Tour and Travels',
          content: `We offer reliable and comfortable taxi services from ${displayFrom} to ${displayTo}. Our fleet includes various vehicle options to suit your travel needs, from compact sedans to spacious SUVs. All vehicles are well-maintained and driven by experienced professionals.`
        }
      ]
    }
  };

  const { from, to, companyName, phone, days, vehicles, visitorsGuide } = effectivePackageData;

  const handleSelectCar = (vehicle) => {
    navigate('/car-details', {
      state: {
        vehicle: {
          ...vehicle,
          name: vehicle?.type,
          image: vehicle?.image,
          price: vehicle?.price
        },
        route: {
          from: displayFrom,
          to: displayTo
        },
        from: displayFrom,
        to: displayTo,
        vehicleName: vehicle?.type,
        price: vehicle?.price,
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        image: vehicle?.image,
        alt: vehicle?.alt
      }
    });
  };

  return (
    <>
      <SEO
        title={`${displayFrom} to ${displayTo} Taxi Tour Packages - Book Now | ${companyName}`}
        description={`Book ${displayFrom} to ${displayTo} taxi tour packages. Choose from 2-8 day packages with various vehicles. Comfortable travel with professional drivers.`}
        keywords={`${displayFrom} to ${displayTo} tour package, ${displayFrom} ${displayTo} taxi, ${displayTo} tour from ${displayFrom}`}
        canonicalUrl={`${window.location?.origin}/tour-package-booking/${cityName}/${destination}`}
        ogTitle={`${displayFrom} to ${displayTo} Taxi Tour Packages - Book Now | ${companyName}`}
        ogDescription={`Book ${displayFrom} to ${displayTo} taxi tour packages. Choose from 2-8 day packages with various vehicles. Comfortable travel with professional drivers.`}
        ogUrl={`${window.location?.origin}/tour-package-booking/${cityName}/${destination}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          "name": companyName,
          "description": `${displayFrom} to ${displayTo} tour package booking`
        }}
      />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section with Day Tabs */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-16 mb-12">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-2">
                {displayFrom} to {displayTo} Taxi Tour Packages
              </h1>
              
              {/* Day Tabs */}
              <div className="flex flex-wrap justify-center gap-4">
                {days?.map((day) => (
                  <button
                    key={day?.id}
                    onClick={() => setSelectedDay(day?.id)}
                    className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                      selectedDay === day?.id
                        ? 'bg-yellow-400 text-gray-900' :'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {day?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Vehicle Cards */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 gap-6 max-w-6xl mx-auto">
              {vehicles?.map((vehicle, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
                >
                  <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                    {/* Vehicle Image */}
                    <div className="w-full md:w-64 h-48 flex-shrink-0">
                      <AppImage
                        src={vehicle?.image}
                        alt={vehicle?.alt}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 flex flex-col md:flex-row items-center justify-between gap-6 w-full">
                      {/* Car Type */}
                      <div className="text-center md:text-left">
                        <p className="text-sm text-gray-500 font-medium uppercase mb-1">CAR TYPE</p>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{vehicle?.type}</h3>
                      </div>

                      {/* Specifications */}
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Luggage className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.luggage} Luggage</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Users className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.seats} Seats</p>
                          </div>
                        </div>
                      </div>

                      {/* Price and Button */}
                      <div className="flex flex-col items-center gap-3">
                        <div className="text-right">
                          <p className="text-3xl font-bold text-green-600">₹{vehicle?.price?.toLocaleString('en-IN')}/-</p>
                          <p className="text-sm text-gray-500">Inc. of Taxes*</p>
                        </div>
                        <button
                          onClick={() => handleSelectCar(vehicle)}
                          className="px-8 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all duration-200 whitespace-nowrap"
                        >
                          Select Car
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visitor's Guide Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-orange-500 mb-6 text-center">
                Visitor's Guide
              </h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <h3 className="text-2xl font-bold text-gray-800">
                  {visitorsGuide?.title}
                </h3>
                
                <p className="text-base">
                  {visitorsGuide?.intro}
                </p>

                {visitorsGuide?.sections?.map((section, index) => (
                  <div key={index} className="space-y-4">
                    <h3 className="text-2xl font-bold text-gray-800 mt-8">
                      {section?.title}
                    </h3>
                    
                    {section?.content && (
                      <p className="text-base">
                        {section?.content}
                      </p>
                    )}

                    {section?.subsections && (
                      <div className="space-y-4">
                        {section?.subsections?.map((subsection, subIndex) => (
                          <div key={subIndex}>
                            <h4 className="text-lg font-bold text-gray-800">
                              {subsection?.subtitle}
                            </h4>
                            <p className="text-base mt-2">
                              {subsection?.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Card Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-xl p-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-6">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {phone}
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TourPackageBooking;